/*
 * Centauri C01 product bar — sticky "pop-up" scroll lock.
 * Progressive enhancement, no framework. No-op when no sticky bar is present.
 */
(function () {
  'use strict';

  var VERSION = '2026-08-01-eva-hero-trigger-v7';
  if (window.__centauriC01ProductBar && window.__centauriC01ProductBar.version === VERSION) {
    window.__centauriC01ProductBar.init();
    return;
  }

  var THRESHOLD = 8;
  var entries = [];
  var fallbackListening = false;
  var fallbackFrame = 0;
  var scrollStateSentinel = null;
  var scrollStateObserver = null;
  var themeHeaderObserver = null;
  var themeHeaderResizeObserver = null;
  var scrolled = false;

  function matches(node, selector) {
    if (!node || node.nodeType !== 1) return false;
    var fn = node.matches || node.msMatchesSelector || node.webkitMatchesSelector;
    return fn ? fn.call(node, selector) : false;
  }

  function findBars(scope) {
    var target = scope && scope.querySelectorAll ? scope : document;
    var bars = matches(scope, '[data-centauri-product-bar-sticky="true"]') ? [scope] : [];
    var found = target.querySelectorAll('[data-centauri-product-bar-sticky="true"]');
    for (var i = 0; i < found.length; i++) bars.push(found[i]);
    return bars;
  }

  function observerSupport() {
    return 'IntersectionObserver' in window && 'ResizeObserver' in window;
  }

  function cssPixelValue(name) {
    var value = window.getComputedStyle(document.documentElement).getPropertyValue(name);
    var parsed = parseFloat(value);
    return Number.isFinite(parsed) ? Math.max(0, parsed) : 0;
  }

  function getStickyTop() {
    if (window.matchMedia('(max-width: 1139px)').matches) {
      return cssPixelValue('--mobile-sticky-header-height');
    }

    var themeHeader = document.querySelector('.xo-header');
    var themeSticky = themeHeader && themeHeader.querySelector('xo-sticky[xo-is-sticky]:not([xo-is-sticky="false"])');
    var themeStickyInner = themeSticky && themeSticky.shadowRoot
      ? themeSticky.shadowRoot.querySelector('[part="inner"]')
      : null;
    if (themeStickyInner && themeStickyInner.getBoundingClientRect) {
      return Math.max(0, themeStickyInner.getBoundingClientRect().bottom);
    }

    /* Before the theme header becomes sticky it still scrolls out of view. Its
       live bottom edge is the only offset that keeps this bar attached to it;
       --xo-top-offset remains at the initial page-load value and exposes the
       white section background while the header is moving. */
    if (themeHeader && themeHeader.getBoundingClientRect) {
      return Math.max(0, themeHeader.getBoundingClientRect().bottom);
    }

    return cssPixelValue('--xo-top-offset');
  }

  function syncStickyTop(entry) {
    if (!entry || !entry.wrapper) return 0;
    var top = getStickyTop();
    entry.wrapper.style.setProperty('--centauri-product-bar-sticky-top', top + 'px');
    return top;
  }

  function getDelayedPinTarget(entry) {
    if (!entry || !window.matchMedia('(min-width: 1140px)').matches) return null;
    if (!entry.campaignRoot || !entry.campaignRoot.isConnected) return null;

    if (!entry.delayedPinTarget || !entry.delayedPinTarget.isConnected) {
      entry.delayedPinTarget = entry.campaignRoot.querySelector('[data-eva-section="s02"]');
    }

    return entry.delayedPinTarget;
  }

  function shouldPin(entry, stickyTop) {
    var delayedPinTarget = getDelayedPinTarget(entry);
    if (delayedPinTarget && delayedPinTarget.getBoundingClientRect) {
      return delayedPinTarget.getBoundingClientRect().bottom <= stickyTop;
    }

    return entry.sentinel.getBoundingClientRect().top <= stickyTop;
  }

  function updateSpacer(entry, height) {
    if (!entry || !entry.spacer) return;
    var nextHeight = Math.max(0, Math.ceil(Number(height) || 0));

    /* The desktop header drops the announcement-bar height when it becomes
       sticky. Keep only the part of the original slot that still sits behind
       the fixed product bar, otherwise the abandoned part of the slot appears
       as a white band between S01 and S02 during that transition. */
    if (entry.pinned && entry.wrapper && entry.sentinel) {
      var wrapperRect = entry.wrapper.getBoundingClientRect();
      var sentinelRect = entry.sentinel.getBoundingClientRect();
      var coveredSlotHeight = Math.max(0, Math.ceil(wrapperRect.bottom - sentinelRect.top));
      nextHeight = Math.min(nextHeight, coveredSlotHeight);
    }

    entry.spacer.style.height = nextHeight ? nextHeight + 'px' : '';
  }

  function measureSpacer(entry) {
    if (!entry || !entry.wrapper || !entry.wrapper.getBoundingClientRect) return;
    updateSpacer(entry, entry.wrapper.getBoundingClientRect().height);
  }

  function setPinned(entry, pinned) {
    if (!entry || entry.pinned === pinned) return;
    entry.pinned = pinned;
    if (pinned) {
      measureSpacer(entry);
      entry.spacer.hidden = false;
      entry.wrapper.classList.add('is-pinned');
    } else {
      entry.wrapper.classList.remove('is-pinned');
      entry.spacer.hidden = true;
      entry.spacer.style.height = '';
    }
  }

  function setScrolled(entry, active) {
    if (!entry || !entry.bar) return;
    entry.bar.classList.toggle('is-scrolled', !!active);
  }

  function applyScrolledState() {
    for (var i = 0; i < entries.length; i++) setScrolled(entries[i], scrolled);
  }

  function clearScrollStateObserver() {
    if (scrollStateObserver && scrollStateObserver.disconnect) scrollStateObserver.disconnect();
    scrollStateObserver = null;
    if (scrollStateSentinel && scrollStateSentinel.parentNode) scrollStateSentinel.parentNode.removeChild(scrollStateSentinel);
    scrollStateSentinel = null;
    scrolled = false;
  }

  function syncScrollStateObserver() {
    if (!observerSupport() || !entries.length || !document.body) {
      clearScrollStateObserver();
      applyScrolledState();
      return;
    }
    if (scrollStateObserver) return;

    scrollStateSentinel = document.createElement('div');
    scrollStateSentinel.className = 'centauri-c01-product-bar-scroll-sentinel';
    scrollStateSentinel.setAttribute('aria-hidden', 'true');
    scrollStateSentinel.style.cssText = 'position:absolute;top:' + THRESHOLD + 'px;left:0;width:1px;height:1px;margin:0;padding:0;pointer-events:none;';
    document.body.insertBefore(scrollStateSentinel, document.body.firstChild || null);

    scrollStateObserver = new IntersectionObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var record = records[i];
        if (record.target !== scrollStateSentinel) continue;
        scrolled = !record.isIntersecting && record.boundingClientRect.top < 0;
        applyScrolledState();
        break;
      }
    }, { threshold: 0 });
    scrollStateObserver.observe(scrollStateSentinel);
  }

  function refreshFallbackEntry(entry) {
    if (!entry || !entry.sentinel || !entry.sentinel.getBoundingClientRect) return;
    var stickyTop = syncStickyTop(entry);
    setPinned(entry, shouldPin(entry, stickyTop));
    setScrolled(entry, window.scrollY > THRESHOLD);
    if (entry.pinned) measureSpacer(entry);
  }

  function flushFallback() {
    fallbackFrame = 0;
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].fallback) refreshFallbackEntry(entries[i]);
    }
  }

  function scheduleFallback() {
    if (fallbackFrame) return;
    fallbackFrame = window.requestAnimationFrame(flushFallback);
  }

  function scheduleNavigationRefresh() {
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(scheduleFallback);
    });
  }

  function clearThemeHeaderObservers() {
    if (themeHeaderObserver) themeHeaderObserver.disconnect();
    if (themeHeaderResizeObserver) themeHeaderResizeObserver.disconnect();
    themeHeaderObserver = null;
    themeHeaderResizeObserver = null;
  }

  function syncThemeHeaderObservers() {
    clearThemeHeaderObservers();
    var themeHeader = document.querySelector('.xo-header');
    if (!themeHeader) return;

    if ('MutationObserver' in window) {
      themeHeaderObserver = new MutationObserver(scheduleFallback);
      themeHeaderObserver.observe(themeHeader, {
        attributes: true,
        subtree: true,
        attributeFilter: ['xo-is-sticky', 'class', 'style', 'hidden']
      });
    }

    if (!('ResizeObserver' in window)) return;
    themeHeaderResizeObserver = new ResizeObserver(scheduleFallback);
    themeHeaderResizeObserver.observe(themeHeader);

    var announcementBars = document.querySelectorAll('.xo-announcement-bar');
    for (var i = 0; i < announcementBars.length; i++) {
      themeHeaderResizeObserver.observe(announcementBars[i]);
    }

    var stickyHeaders = themeHeader.querySelectorAll('xo-sticky');
    for (var j = 0; j < stickyHeaders.length; j++) {
      themeHeaderResizeObserver.observe(stickyHeaders[j]);
      var stickyInner = stickyHeaders[j].shadowRoot
        ? stickyHeaders[j].shadowRoot.querySelector('[part="inner"]')
        : null;
      if (stickyInner) themeHeaderResizeObserver.observe(stickyInner);
    }
  }

  function syncFallbackListeners() {
    var needsFallback = false;
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].fallback) {
        needsFallback = true;
        break;
      }
    }

    if (needsFallback && !fallbackListening) {
      fallbackListening = true;
      window.addEventListener('scroll', scheduleFallback, { passive: true });
      window.addEventListener('resize', scheduleFallback, { passive: true });
    } else if (!needsFallback && fallbackListening) {
      fallbackListening = false;
      window.removeEventListener('scroll', scheduleFallback);
      window.removeEventListener('resize', scheduleFallback);
      if (fallbackFrame) window.cancelAnimationFrame(fallbackFrame);
      fallbackFrame = 0;
    }
  }

  function observeEntry(entry) {
    if ('ResizeObserver' in window) {
      entry.resizeObserver = new ResizeObserver(function (records) {
        if (!records || !records.length) return;
        var record = records[0];
        var box = record.borderBoxSize;
        var height = box && box.length ? box[0].blockSize : (box && box.blockSize);
        if (!Number.isFinite(height)) height = entry.wrapper.getBoundingClientRect().height;
        updateSpacer(entry, height);
      });
      entry.resizeObserver.observe(entry.wrapper);
    }

    /* The theme header height is responsive and can change independently of
       this bar, so use the rAF-throttled path for pinning in every browser. */
    entry.fallback = true;
    refreshFallbackEntry(entry);
  }

  function register(bar) {
    if (!bar || bar.dataset.c01StickyReady === 'true') return;
    var wrapper = bar.closest('.centauri-c01-product-bar-block') || bar;
    if (!wrapper.parentNode) return;
    var host = wrapper.closest('.xo-section, .shopify-section');
    if (host) host.classList.add('centauri-c01-product-bar-host--sticky');

    var sentinel = document.createElement('div');
    sentinel.className = 'centauri-c01-product-bar-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    sentinel.style.cssText = 'position:absolute;width:1px;height:1px;margin:0;padding:0;pointer-events:none;';

    var spacer = document.createElement('div');
    spacer.className = 'centauri-c01-product-bar-spacer';
    spacer.setAttribute('aria-hidden', 'true');
    spacer.hidden = true;

    wrapper.parentNode.insertBefore(sentinel, wrapper);
    wrapper.parentNode.insertBefore(spacer, wrapper);
    bar.dataset.c01StickyReady = 'true';

    var entry = {
      bar: bar,
      wrapper: wrapper,
      host: host,
      campaignRoot: bar.closest('.centauri-eva-asuka'),
      delayedPinTarget: null,
      sentinel: sentinel,
      spacer: spacer,
      pinned: false,
      fallback: false,
      pinObserver: null,
      resizeObserver: null
    };
    entries.push(entry);
    observeEntry(entry);
    setScrolled(entry, scrolled);
  }

  function disposeEntry(entry) {
    if (!entry) return;
    if (entry.pinObserver && entry.pinObserver.disconnect) entry.pinObserver.disconnect();
    if (entry.resizeObserver && entry.resizeObserver.disconnect) entry.resizeObserver.disconnect();
    setPinned(entry, false);
    setScrolled(entry, false);
    if (entry.bar) delete entry.bar.dataset.c01StickyReady;
    if (entry.host) entry.host.classList.remove('centauri-c01-product-bar-host--sticky');
    if (entry.sentinel && entry.sentinel.parentNode) entry.sentinel.parentNode.removeChild(entry.sentinel);
    if (entry.spacer && entry.spacer.parentNode) entry.spacer.parentNode.removeChild(entry.spacer);
  }

  function belongsToScope(entry, scope) {
    if (!scope || scope === document) return true;
    if (scope === entry.bar || scope === entry.wrapper) return true;
    return !!(scope.contains && (scope.contains(entry.bar) || scope.contains(entry.wrapper)));
  }

  function destroy(scope) {
    var live = [];
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (belongsToScope(entry, scope)) disposeEntry(entry);
      else live.push(entry);
    }
    entries = live;
    syncFallbackListeners();
    syncScrollStateObserver();
    if (!entries.length) clearThemeHeaderObservers();
  }

  function pruneDetached() {
    var live = [];
    for (var i = 0; i < entries.length; i++) {
      var entry = entries[i];
      if (entry.wrapper.isConnected && entry.bar.isConnected) live.push(entry);
      else disposeEntry(entry);
    }
    entries = live;
    syncFallbackListeners();
    syncScrollStateObserver();
    if (!entries.length) clearThemeHeaderObservers();
  }

  function init(scope) {
    pruneDetached();
    var bars = findBars(scope || document);
    for (var i = 0; i < bars.length; i++) register(bars[i]);
    syncFallbackListeners();
    syncScrollStateObserver();
    syncThemeHeaderObservers();
  }

  window.__centauriC01ProductBar = { version: VERSION, init: init, destroy: destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(document); });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event && event.target);
  });
  document.addEventListener('shopify:block:select', function () { init(document); });
  document.addEventListener('shopify:section:unload', function (event) {
    destroy(event && event.target);
  });
  window.addEventListener('pageshow', scheduleNavigationRefresh, { once: true });
})();
